// src/modules/module_step4.tsx
import React, {
  useEffect,
  useState,
  useCallback,
  useRef
} from 'react';
import config from '../config/config';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  CardSubtitle
} from '@progress/kendo-react-layout';
import {
  RadioGroup,
  RadioGroupChangeEvent,
  TextArea
} from '@progress/kendo-react-inputs';
import styled from 'styled-components';
import { Button } from '@progress/kendo-react-buttons';
import axios from 'axios';
import { useForm } from '../contexts/FormContext';
import {
  Notification,
  NotificationGroup
} from '@progress/kendo-react-notification';
import GetUserAvatar from '../components/GetUserAvatar';

/* ─────────────────────────────────────────────── */
/* Styled components & Datenquelle                 */
/* ─────────────────────────────────────────────── */

const radioData = [
  {
    label: 'Der Verdacht auf Kampfmittel hat sich nicht bestätigt',
    value: 'option1'
  },
  {
    label: 'Vorgefundene Kampfmittel wurden beräumt / übergeben',
    value: 'option2'
  },
  {
    label: 'Kampfmittel wurden nicht / nicht vollständig beräumt weil:',
    value: 'option3'
  },
  {
    label: 'Der Verdacht auf Kampfmittel hat sich bestätigt',
    value: 'option4'
  },
  {
    label: 'Sonstiges',
    value: 'option5'
  },
  {
    label: 'Ungeklärt',
    value: 'option6'
  }
];

/* Optionen, bei denen das Anmerkungen-Textfeld erscheint und gespeichert wird */
const NOTE_OPTIONS = ['option3', 'option5'];

const StyledButton = styled(Button)`
  font-weight: bold;
  font-size: 20px;
`;

const StyledRadioGroup = styled(RadioGroup)`
  .k-radio-list {
    width: 100%;
  }
  .k-radio-item,
  .k-radio-list-item {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    padding: 10px 0;
    border-bottom: 1px dashed #d1d5db;
  }
  .k-radio-item input[type='radio'],
  .k-radio-list-item input[type='radio'],
  .k-radio-item .k-radio,
  .k-radio-list-item .k-radio {
    margin-top: 0.4em;
  }
  .k-radio-item:last-child,
  .k-radio-list-item:last-child {
    border-bottom: none;
  }
`;

/* ─────────────────────────────────────────────── */
/* Hauptkomponente                                 */
/* ─────────────────────────────────────────────── */
const ModuleStep4: React.FC = () => {
  const { formData } = useForm();

  /* ---------- State & Refs ------------------------------------------- */
  const [selectedValue, setSelectedValue] = useState('');
  const [summaryText, setSummaryText]     = useState('');
  const [showNotification, setShowNotification] = useState(false);

  const selectedRef = useRef('');
  const summaryRef  = useRef('');
  const isDirty     = useRef(false);

  /* ---------- 1) Gespeicherte Werte laden ---------------------------- */
  useEffect(() => {
    if (!formData.jobID) return;

    axios
      .get(`${config.host}/jobs/${encodeURIComponent(formData.jobID)}`)
      .then(res => {
        const job = res.data;
        const storedLabel  = job.Summary || '';
        const storedNotes  = job.SummaryNotes || '';

        const optionVal =
          radioData.find(r => r.label === storedLabel)?.value || '';

        setSelectedValue(optionVal);
        setSummaryText(storedNotes);

        selectedRef.current = optionVal;
        summaryRef.current  = storedNotes;
        isDirty.current     = false;
      })
      .catch(err =>
        console.error('❌ Fehler beim Laden von Summary:', err)
      );
  }, [formData.jobID]);

  /* ---------- 2) Eingabe-Handler ------------------------------------- */
  const handleRadioChange = (e: RadioGroupChangeEvent) => {
    setSelectedValue(e.value);
    selectedRef.current = e.value;
    isDirty.current = true;
  };

  const handleTextChange = (e: any) => {
    setSummaryText(e.value);
    summaryRef.current = e.value;
    isDirty.current = true;
  };

  /* ---------- 3) Speichern ------------------------------------------- */
  const saveSummary = useCallback(async (): Promise<boolean> => {
    if (!formData.jobID || !isDirty.current) return false;

    const selectedLabel =
      radioData.find(o => o.value === selectedRef.current)?.label || '';

    try {
      await axios.put(
        `${config.host}/jobs/${encodeURIComponent(formData.jobID)}`,
        {
          Summary: selectedLabel,
          SummaryNotes: NOTE_OPTIONS.includes(selectedRef.current)
            ? summaryRef.current
            : ''
        }
      );
      isDirty.current = false;
      return true;
    } catch (err) {
      console.error('❌ Fehler beim Speichern von Summary:', err);
      return false;
    }
  }, [formData.jobID]);

  /* ---------- 4) Auto-Save bei Unmount / Refresh --------------------- */
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveSummary().catch(() => {});
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      saveSummary().catch(() => {});
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formData.jobID, saveSummary]);

  /* ---------- 5) Manuelles Speichern --------------------------------- */
  const handleManualSave = async () => {
    const ok = await saveSummary();
    if (ok) {
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    }
  };

  /* ---------- 6) Render ---------------------------------------------- */
  return (
    <div style={{ padding: 20 }}>
      {showNotification && (
        <NotificationGroup
          style={{
            position: 'fixed',
            top: 30,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000
          }}
        >
          <Notification type={{ style: 'success', icon: true }}>
            <span>Kampfmittelbefund erfolgreich gespeichert.</span>
          </Notification>
        </NotificationGroup>
      )}

      <Card orientation="vertical" className="custom-card">
        <CardHeader className="k-hbox custom-card-header">
          <GetUserAvatar />
          <div>
            <CardTitle>Step 4: Abschluss</CardTitle>
            <CardSubtitle>
              Bitte wählen Sie die entsprechende Option aus
            </CardSubtitle>
          </div>
        </CardHeader>

        <CardBody>
          <div style={{ marginBottom: 20 }}>
            <StyledRadioGroup
              data={radioData}
              value={selectedValue}
              onChange={handleRadioChange}
              layout="vertical"
            />
          </div>

          {NOTE_OPTIONS.includes(selectedValue) && (
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="summaryTextArea">Anmerkungen</label>
              <TextArea
                id="summaryTextArea"
                placeholder="Bitte geben Sie den Grund an..."
                style={{ width: '100%', height: 150 }}
                value={summaryText}
                onChange={handleTextChange}
              />
            </div>
          )}

          <StyledButton
            themeColor="primary"
            style={{ width: '100%' }}
            onClick={handleManualSave}
          >
            Speichern
          </StyledButton>
        </CardBody>
      </Card>
    </div>
  );
};

export default ModuleStep4;
